import React, {useState, useEffect} from 'react';
import {Box, Paper, Grid, Typography} from '@mui/material';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

type MatchPair = {
  id: number;
  left: string;
  right: string;
};

type MatchTheFollowingProps = {
  layout: number;
  selectedAnswers: any;
  question: {
    id: number;
    QuestionText: string;
    correctMatches: Array<{
      id: number;
      match_the_following_left_value: string;
    }>;
    additionalChoices: Array<{
      id: number;
      match_the_following_right_value: string;
    }>;
  };
  onAnswerChange: (questionId: number, updatedAnswers: MatchPair[]) => void;
};

const MatchTheFollowingQuestion: React.FC<MatchTheFollowingProps> = ({
  layout,
  question,
  selectedAnswers,
  onAnswerChange
}) => {
  // Shuffle function to randomize the right-side options initially

  // Fixed left values
  const shuffleArray = (array: any[]) =>
    [...array].sort(() => Math.random() - 0.5);

  // Fixed left values
  const leftValues: MatchPair[] = question.correctMatches.map(match => ({
    id: match.id,
    left: match.match_the_following_left_value,
    right: '' // Will be assigned from shuffled choices
  }));

  // Function to generate unique incorrect pairs
  const generateIncorrectPairs = () => {
    let shuffledRightValues = shuffleArray([...question.additionalChoices]);

    // Set to track assigned incorrect values
    const assignedRightValues = new Set<number>();

    const incorrectPairs: MatchPair[] = [];

    for (const pair of leftValues) {
      // Filter to get available incorrect choices **before searching**
      let availableChoices = shuffledRightValues.filter(
        r => r.id !== pair.id && !assignedRightValues.has(r.id)
      );

      // If no valid incorrectRight is found, reshuffle and retry
      if (availableChoices.length === 0) {
        shuffledRightValues = shuffleArray([...question.additionalChoices]);
        availableChoices = shuffledRightValues.filter(
          r => r.id !== pair.id && !assignedRightValues.has(r.id)
        );
      }

      // **Select incorrectRight AFTER filtering**
      const incorrectRight =
        availableChoices.length > 0 ? availableChoices[0] : undefined;

      // **Final check**: If still undefined, SKIP adding this pair
      if (!incorrectRight) {
        console.warn(
          `No valid incorrect match found for left value: ${pair.left}, skipping entry.`
        );
        continue; // Skip adding this pair
      }

      // ✅ Only mark as used if valid
      assignedRightValues.add(incorrectRight.id);

      incorrectPairs.push({
        ...pair,
        right: incorrectRight.match_the_following_right_value // ✅ Correctly assigning value
      });
    }

    return incorrectPairs;
  };

  // Generate the incorrect pairs

  const [answers, setAnswers] = useState<any[]>([]);
  console.log('pairs', answers);
  // Ensure answers are shuffled only once when the component mounts
  useEffect(() => {
    if (selectedAnswers.length !== 0) {
      setAnswers(selectedAnswers);
    } else {
      const initialPairs = generateIncorrectPairs();
      setAnswers(initialPairs);
      if (layout === 1) {
        onAnswerChange(question.id, initialPairs);
      }
    }
  }, []);

  // Handle Drag and Drop (Update right values without changing order)
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    // Extract only right values
    const newRightValues = answers.map(pair => pair.right);
    const [movedValue] = newRightValues.splice(result.source.index, 1); // Remove moved value
    newRightValues.splice(result.destination.index, 0, movedValue); // Insert at new position

    // Create a new mapping while keeping left values unchanged
    const updatedAnswers = answers.map((pair, index) => ({
      ...pair,
      right: newRightValues[index]
    }));

    setAnswers(updatedAnswers);
    onAnswerChange(question.id, updatedAnswers);
  };

  return (
    <Box sx={{mt: 2, p: 2, border: '1px solid #ddd', borderRadius: '8px'}}>
      <Grid container spacing={2}>
        {/* Left Side (Fixed Questions) */}
        <Grid item xs={5}>
          {answers.map(
            pair =>
              pair?.left && (
                <Paper
                  key={pair.id}
                  sx={{
                    p: 1,
                    mb: 1,
                    textAlign: 'left',
                    backgroundColor: '#f0f0f0'
                  }}>
                  {pair?.left}
                </Paper>
              )
          )}
        </Grid>

        {/* Right Side (Draggable Answers) */}
        <Grid item xs={5}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="answers">
              {provided => (
                <Box {...provided.droppableProps} ref={provided.innerRef}>
                  {answers.map((pair, index) => (
                    <Draggable
                      key={pair.right}
                      draggableId={pair.right}
                      index={index}>
                      {provided => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            p: 1,
                            mb: 1,
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'grab',
                            backgroundColor: '#f0f0f0'
                          }}>
                          <DragIndicatorIcon sx={{mr: 1}} />
                          {pair.right}
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MatchTheFollowingQuestion;
